import { addMethod, AnySchema, mixed, string } from "yup";

import { regexes } from "./regex";

/**
 * Validates that a string contains only alphabetic characters.
 */
addMethod(string, "alpha", function () {
  return this.matches(regexes.alpha);
});

/**
 * Validates that a string contains only alphabetic characters, hashtags, hyphens, periods, and spaces.
 */
addMethod(string, "alphaHashtagHyphenPeriodSpace", function () {
  return this.matches(regexes.alphaHashtagHyphenPeriodSpace);
});

/**
 * Validates that a string contains only alphabetic characters, hyphens, periods, and spaces.
 */
addMethod(string, "alphaHyphenPeriodSpace", function () {
  return this.matches(regexes.alphaHyphenPeriodSpace);
});

/**
 * Validates that a string contains only alphanumeric characters.
 */
addMethod(string, "alphaNumeric", function () {
  return this.matches(regexes.alphaNumeric);
});

/**
 * Validates that a string contains only alphanumeric characters, hyphens, periods, and spaces.
 */
addMethod(string, "alphaNumericHyphenPeriodSpace", function () {
  return this.matches(regexes.alphaNumericHyphenPeriodSpace);
});

/**
 * Validates that a string contains only alphanumeric characters, hashtags, hyphens, periods, and spaces.
 */
addMethod(string, "alphaNumericHashtagHyphenPeriodSpace", function () {
  return this.matches(regexes.alphaNumericHashtagHyphenPeriodSpace);
});

/**
 * Validates that a string contains only alphanumeric characters and punctuation.
 */
addMethod(string, "alphaNumericPunct", function () {
  return this.matches(regexes.alphaNumericPunct);
});

/**
 * Validates that a string contains only alphanumeric characters, punctuation, and spaces.
 */
addMethod(string, "alphaNumericPunctSpace", function () {
  return this.matches(regexes.alphaNumericPunctSpace);
});

/**
 * Validates that a string contains only alphanumeric characters, punctuation, and whitespace.
 */
addMethod(string, "alphaNumericPunctWhiteSpace", function () {
  return this.matches(regexes.alphaNumericPunctWhiteSpace);
});

/**
 * Validates that a string contains only alphanumeric characters and spaces.
 */
addMethod(string, "alphaNumericSpace", function () {
  return this.matches(regexes.alphaNumericSpace);
});

/**
 * Validates that a string contains only alphabetic characters and spaces.
 */
addMethod(string, "alphaSpace", function () {
  return this.matches(regexes.alphaSpace);
});

/**
 * Validates that a value matches at least one of the provided schemas synchronously.
 * @param schemas - Array of yup schemas to validate against
 * @param errorMessage - Custom error message to display on validation failure
 */
addMethod(mixed, "oneOfSchemas", function (schemas: AnySchema[], errorMessage: string) {
  return this.test({
    message: errorMessage,
    test: (value) => schemas.some((schema) => schema.isValidSync(value)),
  });
});

/**
 * Validates that a string does not contain backslash characters.
 */
addMethod(string, "noBackslash", function () {
  return this.matches(regexes.noBackslash);
});

/**
 * Validates that a string does not contain double quote characters.
 */
addMethod(string, "noDoubleQuotes", function () {
  return this.matches(regexes.noDoubleQuotes);
});

/**
 * Validates that a string contains only numeric characters.
 */
addMethod(string, "numeric", function () {
  return this.matches(regexes.numeric);
});

/**
 * Validates that a string is a valid phone number format.
 */
addMethod(string, "phone", function () {
  return this.matches(regexes.phone);
});

/**
 * Validates that a string represents a positive number.
 */
addMethod(string, "positive", function () {
  return this.matches(regexes.positive);
});

/**
 * Transforms a string by replacing matches of a pattern with a replacement string.
 * @param pattern - Regular expression pattern to match
 * @param replacement - String to replace matches with
 */
addMethod(string, "replace", function (pattern: RegExp, replacement: string) {
  return this.transform((value) => value.replace(pattern, replacement));
});

/**
 * Transforms a string by removing all whitespace characters.
 */
addMethod(string, "stripAllWhitespace", function () {
  return this.replace(/\s+/g, ""); // NOSONAR
});

/**
 * Validates that a string is in a valid date format.
 * @param errorMessage - Optional custom error message
 */
addMethod(string, "validDate", function (errorMessage?: string) {
  return this.matches(regexes.validDate, errorMessage);
});

/**
 * Validates that a string is in a valid email format.
 * @param errorMessage - Optional custom error message
 */
addMethod(string, "validEmail", function (errorMessage?: string) {
  return this.matches(regexes.validEmail, errorMessage);
});

declare module "yup" {
  interface StringSchema {
    /**
     * Validates that a string contains only alphabetic characters.
     */
    alpha(): this;
    /**
     * Validates that a string contains only alphabetic characters, hashtags, hyphens, periods, and spaces.
     */
    alphaHashtagHyphenPeriodSpace(): this;
    /**
     * Validates that a string contains only alphabetic characters, hyphens, periods, and spaces.
     */
    alphaHyphenPeriodSpace(): this;
    /**
     * Validates that a string contains only alphanumeric characters.
     */
    alphaNumeric(): this;
    /**
     * Validates that a string contains only alphanumeric characters, hyphens, periods, and spaces.
     */
    alphaNumericHyphenPeriodSpace(): this;
    /**
     * Validates that a string contains only alphanumeric characters, hashtags, hyphens, periods, and spaces.
     */
    alphaNumericHashtagHyphenPeriodSpace(): this;
    /**
     * Validates that a string contains only alphanumeric characters and punctuation.
     */
    alphaNumericPunct(): this;
    /**
     * Validates that a string contains only alphanumeric characters, punctuation, and spaces.
     */
    alphaNumericPunctSpace(): this;
    /**
     * Validates that a string contains only alphanumeric characters, punctuation, and whitespace.
     */
    alphaNumericPunctWhiteSpace(): this;
    /**
     * Validates that a string contains only alphanumeric characters and spaces.
     */
    alphaNumericSpace(): this;
    /**
     * Validates that a string contains only alphabetic characters and spaces.
     */
    alphaSpace(): this;
    /**
     * Validates that a string does not contain backslash characters.
     */
    noBackslash(): this;
    /**
     * Validates that a string does not contain double quote characters.
     */
    noDoubleQuotes(): this;
    /**
     * Validates that a string contains only numeric characters.
     */
    numeric(): this;
    /**
     * Validates that a string is a valid phone number format.
     */
    phone(): this;
    /**
     * Validates that a string represents a positive number.
     */
    positive(): this;
    /**
     * Transforms a string by replacing matches of a pattern with a replacement string.
     * @param pattern - Regular expression pattern to match
     * @param replacement - String to replace matches with
     */
    replace(pattern: RegExp, replacement: string): this;
    /**
     * Transforms a string by removing all whitespace characters.
     */
    stripAllWhitespace(): this;
    /**
     * Validates that a string is in a valid date format.
     * @param errorMessage - Optional custom error message
     */
    validDate(errorMessage?: string): this;
    /**
     * Validates that a string is in a valid email format.
     * @param errorMessage - Optional custom error message
     */
    validEmail(errorMessage?: string): this;
  }
}

export * from "yup";
